import { useState } from 'react'
import { useLocation } from 'react-router-dom'
import { Outlet } from 'react-router-dom'

import { Box, Spinner } from '@factorialco/deprecated-design-system'
import { EnpsRootPageDocument } from 'generated/resources/graphql'

import { observer } from 'mobx-react'

import NotFound from 'modules/core/components/NotFound'
import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useQuery } from 'modules/core/hooks/useQuery'

import { useEngagementContext } from 'modules/engagement/hooks/useEngagementContext'
import { hasEnpsModule } from 'modules/engagement/lib/hasEnpsModule'
import type { AnalyticsFilterForm } from 'modules/enps/hooks/useAnalyticsMultiFilters'
import { useForm } from 'modules/enps/hooks/useForm'
import { useQuestionGroups } from 'modules/enps/hooks/useQuestionGroups'
import CollectionsBootstrap from 'modules/enps/lib/CollectionsBootstrap'
import { EnpsFormContext } from 'modules/enps/lib/EnpsFormContext'

import Upsell from './Upsell'

const Container = observer(() => {
  const { pathname } = useLocation()
  const { data, loading: loadingPolicies } = useQuery(EnpsRootPageDocument)
  const canCreateForms = data?.enps._policies.createForms.can ?? false

  const [analyticsFilter, setAnalyticsFilter] = useState<AnalyticsFilterForm | undefined>()
  const [resultsFilter, setResultsFilter] = useState<AnalyticsFilterForm | undefined>()
  const [refreshForm, setRefreshForm] = useState<boolean>(true)
  const { loading: loadingForm, form } = useForm(
    refreshForm,
    setRefreshForm,
    canCreateForms,
    loadingPolicies
  )
  const { moduleAvailabilities } = useEngagementContext()

  const values = {
    analyticsFilter: analyticsFilter,
    form: form,
    refreshForm,
    setRefreshForm,
    loadingForm: loadingPolicies || loadingForm,
    resultsFilter: resultsFilter,
    setAnalyticsFilter: setAnalyticsFilter,
    setResultsFilter: setResultsFilter,
  }

  if (!hasEnpsModule(moduleAvailabilities)) {
    return <Upsell />
  }

  const { access } = useCurrentSession()
  const { loading: loadingQuestionGroups, questionGroups } = useQuestionGroups({
    formId: form?.id,
    respondentId: access.id,
  })

  const isLoading =
    loadingPolicies || loadingForm || (!form && refreshForm) || loadingQuestionGroups

  if (isLoading) {
    return (
      <Box padding="s48" alignItems="center" justifyContent="center">
        <Spinner />
      </Box>
    )
  }

  if (!form) return <NotFound />

  const enpsForm = form

  const filteredQuestionGroups = questionGroups.filter(
    (questionGroup) => questionGroup.attributes.created_at === enpsForm.attributes.start_on
  )

  const group = filteredQuestionGroups[filteredQuestionGroups.length - 1]

  return (
    <EnpsFormContext.Provider value={values}>
      <Outlet context={{ pathname, form: enpsForm, group }} />
    </EnpsFormContext.Provider>
  )
})

const RootPage = () => {
  return (
    <CollectionsBootstrap>
      <Container />
    </CollectionsBootstrap>
  )
}

export default RootPage
